import React, { useState, useEffect } from 'react';

const API = 'https://api.botanix.in/api';
const LOGO = 'https://botanix.in/wp-content/uploads/2025/12/Botanix-Nature-Resort-Logo.webp';

const C = {
  bg:'#0A1A0F', surface:'#0F1E14', surface2:'#162210',
  border:'rgba(58,175,88,.15)', border2:'rgba(58,175,88,.3)',
  text:'#F0F8F2', text2:'#8AB89A', text3:'#4A6850',
  accent:'#3AAF58', accentDark:'#2E8B47', accentLight:'rgba(58,175,88,.12)',
  amber:'#D4840F', amberBg:'rgba(212,132,15,.12)',
  red:'#C83333', redBg:'rgba(200,51,51,.12)',
  blue:'#2563A8', blueBg:'rgba(37,99,168,.12)',
  purple:'#6B48A8', purpleBg:'rgba(107,72,168,.12)',
  gold:'#F0B800', goldBg:'rgba(240,184,0,.12)',
};

const TIERS = [
  { name:'Seed',      color:'#7A9A80', min:0,     next:1000,  perks:['10 pts per Rs.100 on stays','5 pts on F&B','Birthday bonus points'] },
  { name:'Sapling',   color:'#3AAF58', min:1000,  next:5000,  perks:['1.2x points multiplier','Early check-in (subject to availability)','Welcome drink'] },
  { name:'Bloom',     color:'#2563A8', min:5000,  next:15000, perks:['1.5x points multiplier','Room upgrade priority','Complimentary breakfast once'] },
  { name:'Forest',    color:'#6B48A8', min:15000, next:50000, perks:['2x points multiplier','Guaranteed room upgrade','Late checkout till 2PM','Dedicated host'] },
  { name:'Sanctuary', color:'#D4840F', min:50000, next:null,  perks:['3x points multiplier','Suite upgrade priority','Airport transfer','Exclusive member events','Personal butler'] },
];

const REDEMPTIONS = [
  { id:'discount_500',  label:'Rs.50 Discount',     points:500,  icon:'V', desc:'Valid on any booking' },
  { id:'discount_1000', label:'Rs.100 Discount',    points:1000, icon:'V', desc:'Valid on any booking' },
  { id:'discount_5000', label:'Rs.600 Discount',    points:5000, icon:'V', desc:'Valid on bookings above Rs.2000' },
  { id:'upgrade',       label:'Room Upgrade',        points:2000, icon:'U', desc:'One complimentary upgrade' },
  { id:'free_night',    label:'Free Night Stay',     points:8000, icon:'N', desc:'One night stay (base room)' },
];

const inr = (n) => 'Rs.' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });

const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary: { background:`linear-gradient(135deg,${C.accent},${C.accentDark})`, color:'#fff', border:'none' },
    ghost:   { background:'transparent', color:C.accent, border:`1px solid ${C.accent}` },
    danger:  { background:C.red, color:'#fff', border:'none' },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...v[variant], padding:'11px 22px', borderRadius:10, fontSize:14, fontWeight:700,
        cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
        display:'inline-flex', alignItems:'center', gap:8, ...style }}>
      {children}
    </button>
  );
};

const TierBadge = ({ tier }) => {
  const t = TIERS.find(x => x.name === tier) || TIERS[0];
  return (
    <span style={{ background:`${t.color}22`, color:t.color, border:`1px solid ${t.color}44`,
      padding:'3px 12px', borderRadius:99, fontSize:12, fontWeight:700 }}>
      {t.name}
    </span>
  );
};

const PointsBar = ({ current, tier }) => {
  const t = TIERS.find(x => x.name === tier) || TIERS[0];
  const tIdx = TIERS.indexOf(t);
  const next = TIERS[tIdx + 1];
  if (!next) return <div style={{ fontSize:12, color:C.gold, fontWeight:600, textAlign:'center', marginTop:8 }}>Sanctuary — Highest Tier</div>;
  const pct = Math.min(((current - t.min) / (next.min - t.min)) * 100, 100);
  return (
    <div style={{ marginTop:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.text2, marginBottom:6 }}>
        <span>{t.name} ({current.toLocaleString()} pts)</span>
        <span>{next.name} ({next.min.toLocaleString()} pts)</span>
      </div>
      <div style={{ height:6, background:C.border2, borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:6, width:`${pct}%`, background:`linear-gradient(90deg,${t.color},${next.color})`, borderRadius:99, transition:'width .6s' }} />
      </div>
      <div style={{ fontSize:11, color:C.text3, marginTop:5, textAlign:'center' }}>
        {(next.min - current).toLocaleString()} pts to {next.name}
      </div>
    </div>
  );
};

// ── LOGIN SCREEN ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, onEnroll }) {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    if (phone.length < 10) { setError('Enter valid 10-digit phone number'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/loyalty/member/${phone}`);
      if (!res.ok) { setError('Member not found. Please enroll first.'); setLoading(false); return; }
      const data = await res.json();
      onLogin(data);
    } catch(e) { setError('Connection error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <img src={LOGO} alt="Botanix" style={{ height:60, marginBottom:16 }} onError={e=>e.target.style.display='none'} />
          <div style={{ fontSize:28, fontWeight:800, color:C.text, letterSpacing:'-.02em' }}>Botanix Rewards</div>
          <div style={{ fontSize:14, color:C.text2, marginTop:6 }}>Nature's finest loyalty program</div>
        </div>
        <Card style={{ padding:32 }}>
          <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:20 }}>Welcome Back</div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>Mobile Number</div>
            <input value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
              placeholder="Enter your 10-digit mobile number"
              onKeyDown={e=>e.key==='Enter'&&handleLogin()}
              style={{ width:'100%', padding:'12px 14px', background:C.surface2, border:`1px solid ${C.border2}`,
                borderRadius:10, color:C.text, fontSize:15, outline:'none', boxSizing:'border-box' }} />
          </div>
          {error && <div style={{ color:C.red, fontSize:13, marginBottom:12 }}>{error}</div>}
          <Btn onClick={handleLogin} disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
            {loading ? 'Looking up...' : 'Access My Account'}
          </Btn>
          <div style={{ textAlign:'center', marginTop:20, fontSize:13, color:C.text3 }}>
            Not a member?{' '}
            <span onClick={onEnroll} style={{ color:C.accent, cursor:'pointer', fontWeight:600 }}>Join free</span>
          </div>
        </Card>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginTop:24 }}>
          {[['500', 'Welcome Points'],['5', 'Reward Tiers'],['3x', 'Max Multiplier']].map(([v,l]) => (
            <Card key={l} style={{ padding:'14px 10px', textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:C.accent }}>{v}</div>
              <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>{l}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── ENROLL SCREEN ─────────────────────────────────────────────────────────────
function EnrollScreen({ onBack, onEnrolled }) {
  const [form, setForm] = useState({ name:'', phone:'', email:'', birthday:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const f = k => v => setForm(p => ({ ...p, [k]:v }));

  const handleEnroll = async () => {
    if (!form.name || form.phone.length < 10) { setError('Name and valid phone required'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/loyalty/enroll`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Enrollment failed'); setLoading(false); return; }
      onEnrolled(data);
    } catch(e) { setError('Connection error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <img src={LOGO} alt="Botanix" style={{ height:52, marginBottom:12 }} onError={e=>e.target.style.display='none'} />
          <div style={{ fontSize:26, fontWeight:800, color:C.text }}>Join Botanix Rewards</div>
          <div style={{ fontSize:13, color:C.text2, marginTop:6 }}>Earn 500 welcome points instantly</div>
        </div>
        <Card style={{ padding:28 }}>
          {[['Full Name','name','text','Your full name'],['Mobile Number','phone','tel','10-digit mobile'],['Email (optional)','email','email','your@email.com'],['Birthday (optional)','birthday','date','']].map(([label,key,type,ph]) => (
            <div key={key} style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 }}>{label}</div>
              <input type={type} value={form[key]}
                onChange={e => f(key)(key==='phone'?e.target.value.replace(/\D/g,'').slice(0,10):e.target.value)}
                placeholder={ph}
                style={{ width:'100%', padding:'11px 14px', background:C.surface2, border:`1px solid ${C.border2}`,
                  borderRadius:10, color:C.text, fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
          ))}
          {error && <div style={{ color:C.red, fontSize:13, marginBottom:12 }}>{error}</div>}
          <Btn onClick={handleEnroll} disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
            {loading ? 'Enrolling...' : 'Join & Earn 500 Points'}
          </Btn>
          <div style={{ textAlign:'center', marginTop:16, fontSize:13, color:C.text3 }}>
            Already a member?{' '}
            <span onClick={onBack} style={{ color:C.accent, cursor:'pointer', fontWeight:600 }}>Sign in</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ member, onLogout, onRefresh }) {
  const [tab, setTab] = useState('home');
  const [redeeming, setRedeeming] = useState(null);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState('');

  const tier = TIERS.find(t => t.name === member.tier) || TIERS[0];
  const tIdx = TIERS.indexOf(tier);

  const handleRedeem = async (redemption) => {
    if (member.available_points < redemption.points) { setRedeemMsg('Insufficient points'); return; }
    setRedeemLoading(true); setRedeemMsg('');
    try {
      const res = await fetch(`${API}/loyalty/redeem`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ phone: member.phone, type: redemption.id })
      });
      const data = await res.json();
      if (!res.ok) { setRedeemMsg(data.error || 'Redemption failed'); }
      else { setRedeemMsg(`Voucher created! Code: ${data.voucher_code}`); onRefresh(); }
    } catch(e) { setRedeemMsg('Connection error'); }
    setRedeemLoading(false);
    setRedeeming(null);
  };

  const tabs = [['home','Home'],['points','Points'],['vouchers','Vouchers'],['tiers','Tiers'],['refer','Refer']];

  return (
    <div style={{ minHeight:'100vh', background:C.bg, maxWidth:480, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'16px 20px',
        display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:50 }}>
        <img src={LOGO} alt="Botanix" style={{ height:32 }} onError={e=>e.target.style.display='none'} />
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <TierBadge tier={member.tier} />
          <button onClick={onLogout} style={{ background:'none', border:'none', color:C.text3, cursor:'pointer', fontSize:13 }}>Logout</button>
        </div>
      </div>

      {/* Points Hero */}
      {tab === 'home' && (
        <div style={{ background:`linear-gradient(135deg,${tier.color}22,${C.surface})`, padding:'28px 20px' }}>
          <div style={{ fontSize:13, color:C.text2, marginBottom:4 }}>Welcome back,</div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:16 }}>{member.name}</div>
          <Card style={{ padding:24, marginBottom:0 }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>Available Points</div>
              <div style={{ fontSize:52, fontWeight:800, color:tier.color, letterSpacing:'-.03em', lineHeight:1 }}>
                {(member.available_points||0).toLocaleString()}
              </div>
              <div style={{ fontSize:13, color:C.text2, marginTop:4 }}>Total earned: {(member.total_points||0).toLocaleString()} pts</div>
              <PointsBar current={member.total_points||0} tier={member.tier} />
            </div>
          </Card>

          {/* Quick stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginTop:14 }}>
            {[['Member ID', member.member_code],['Tier', member.tier],['Total Spent', inr(member.total_spent||0)]].map(([l,v]) => (
              <Card key={l} style={{ padding:'12px 10px', textAlign:'center' }}>
                <div style={{ fontSize:11, color:C.text3, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{v}</div>
              </Card>
            ))}
          </div>

          {/* Current tier perks */}
          <Card style={{ padding:20, marginTop:14 }}>
            <div style={{ fontSize:13, fontWeight:700, color:tier.color, marginBottom:12 }}>{tier.name} Member Benefits</div>
            {tier.perks.map(p => (
              <div key={p} style={{ display:'flex', gap:10, marginBottom:8, fontSize:13, color:C.text2 }}>
                <span style={{ color:C.accent, fontWeight:700, flexShrink:0 }}>+</span>{p}
              </div>
            ))}
          </Card>

          {/* Active vouchers preview */}
          {(member.vouchers||[]).filter(v=>v.status==='active').length > 0 && (
            <Card style={{ padding:16, marginTop:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:10 }}>Active Vouchers</div>
              {(member.vouchers||[]).filter(v=>v.status==='active').slice(0,2).map(v => (
                <div key={v.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'10px 12px', background:C.accentLight, borderRadius:8, marginBottom:6 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:C.accent, fontFamily:'monospace' }}>{v.code}</div>
                    <div style={{ fontSize:11, color:C.text3 }}>Valid till {v.valid_until?.slice(0,10)}</div>
                  </div>
                  {v.value > 0 && <div style={{ fontSize:15, fontWeight:800, color:C.accent }}>{inr(v.value)} off</div>}
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Points History */}
      {tab === 'points' && (
        <div style={{ padding:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:16 }}>Points History</div>
          <Card style={{ padding:0, overflow:'hidden' }}>
            {(member.transactions||[]).length === 0 ? (
              <div style={{ padding:40, textAlign:'center', color:C.text3 }}>No transactions yet</div>
            ) : (member.transactions||[]).map(t => (
              <div key={t.id} style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`,
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{t.description}</div>
                  <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{t.category} - {new Date(t.created_at).toLocaleDateString('en-IN')}</div>
                </div>
                <div style={{ fontSize:16, fontWeight:800, color: t.points > 0 ? C.accent : C.red }}>
                  {t.points > 0 ? '+' : ''}{t.points}
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Redeem */}
      {tab === 'vouchers' && (
        <div style={{ padding:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:4 }}>Redeem Points</div>
          <div style={{ fontSize:13, color:C.text2, marginBottom:16 }}>Available: {(member.available_points||0).toLocaleString()} pts</div>
          {redeemMsg && (
            <div style={{ padding:'12px 16px', background: redeemMsg.includes('Voucher') ? C.accentLight : C.redBg,
              border:`1px solid ${redeemMsg.includes('Voucher') ? C.accent : C.red}`, borderRadius:10, marginBottom:14,
              fontSize:13, color: redeemMsg.includes('Voucher') ? C.accent : C.red, fontWeight:600 }}>
              {redeemMsg}
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:28 }}>
            {REDEMPTIONS.map(r => (
              <Card key={r.id} style={{ padding:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{r.label}</div>
                  <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{r.desc}</div>
                  <div style={{ fontSize:12, color:C.accent, fontWeight:700, marginTop:4 }}>{r.points.toLocaleString()} points</div>
                </div>
                <Btn onClick={() => handleRedeem(r)} disabled={member.available_points < r.points || redeemLoading}
                  style={{ padding:'8px 16px', fontSize:12 }}>
                  Redeem
                </Btn>
              </Card>
            ))}
          </div>

          {/* Active vouchers */}
          <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:12 }}>My Vouchers</div>
          {(member.vouchers||[]).filter(v=>v.status==='active').length === 0 ? (
            <Card style={{ padding:30, textAlign:'center', color:C.text3 }}>No active vouchers</Card>
          ) : (member.vouchers||[]).filter(v=>v.status==='active').map(v => (
            <Card key={v.id} style={{ padding:16, marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontFamily:'monospace', fontSize:18, fontWeight:800, color:C.accent, letterSpacing:2 }}>{v.code}</div>
                  <div style={{ fontSize:12, color:C.text3, marginTop:4 }}>Valid till {v.valid_until?.slice(0,10)}</div>
                </div>
                {v.value > 0 && <div style={{ fontSize:22, fontWeight:800, color:C.accent }}>{inr(v.value)} off</div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tiers */}
      {tab === 'tiers' && (
        <div style={{ padding:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:16 }}>Reward Tiers</div>
          {TIERS.map((t, i) => (
            <Card key={t.name} style={{ padding:20, marginBottom:12, border:`1px solid ${t.name===member.tier?t.color:C.border}`,
              background: t.name===member.tier ? `${t.color}11` : C.surface }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:12, height:12, borderRadius:'50%', background:t.color }} />
                  <div style={{ fontSize:16, fontWeight:800, color:t.color }}>{t.name}</div>
                  {t.name===member.tier && <span style={{ fontSize:10, background:t.color, color:'#fff', padding:'2px 8px', borderRadius:99, fontWeight:700 }}>YOUR TIER</span>}
                </div>
                <div style={{ fontSize:12, color:C.text3 }}>{t.min.toLocaleString()}+ pts</div>
              </div>
              <div style={{ fontSize:12, color:C.text2, fontWeight:600, marginBottom:8 }}>x{t.multiplier} points multiplier</div>
              {t.perks.map(p => (
                <div key={p} style={{ fontSize:12, color:C.text2, display:'flex', gap:8, marginBottom:5 }}>
                  <span style={{ color:t.color }}>+</span>{p}
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}

      {/* Refer */}
      {tab === 'refer' && (
        <div style={{ padding:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:8 }}>Refer a Friend</div>
          <div style={{ fontSize:13, color:C.text2, marginBottom:20 }}>Earn 200 bonus points for every friend who enrolls with your code</div>
          <Card style={{ padding:28, textAlign:'center', marginBottom:16 }}>
            <div style={{ fontSize:12, color:C.text3, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>Your Referral Code</div>
            <div style={{ fontSize:36, fontWeight:800, color:C.accent, letterSpacing:6, fontFamily:'monospace', marginBottom:16 }}>
              {member.referral_code || 'LOADING'}
            </div>
            <Btn onClick={() => { navigator.clipboard?.writeText(member.referral_code); }}
              variant="ghost" style={{ justifyContent:'center', width:'100%' }}>
              Copy Code
            </Btn>
          </Card>
          <Card style={{ padding:20 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>How it works</div>
            {[['Share your code','Send your referral code to friends and family'],
              ['Friend enrolls','They sign up using your referral code'],
              ['Both earn','You get 200 pts, they get 500 welcome pts']
            ].map(([t,d],i) => (
              <div key={i} style={{ display:'flex', gap:14, marginBottom:14 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:C.accentLight,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:13,
                  fontWeight:800, color:C.accent, flexShrink:0 }}>{i+1}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{t}</div>
                  <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{d}</div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Bottom Nav */}
      <div style={{ position:'sticky', bottom:0, background:C.surface, borderTop:`1px solid ${C.border}`,
        display:'flex', justifyContent:'space-around', padding:'10px 0' }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ background:'none', border:'none', cursor:'pointer', display:'flex', flexDirection:'column',
              alignItems:'center', gap:3, padding:'4px 12px',
              color: tab===id ? C.accent : C.text3, fontSize:11, fontWeight: tab===id ? 700 : 500 }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background: tab===id ? C.accent : 'transparent' }} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function GuestPortal() {
  const [screen, setScreen] = useState('login');
  const [member, setMember] = useState(null);

  const handleLogin = (data) => { setMember(data); setScreen('dashboard'); };
  const handleEnrolled = (data) => { setMember(data); setScreen('dashboard'); };
  const handleLogout = () => { setMember(null); setScreen('login'); };

  const handleRefresh = async () => {
    if (!member) return;
    try {
      const res = await fetch(`${API}/loyalty/member/${member.phone}`);
      if (res.ok) setMember(await res.json());
    } catch(e) {}
  };

  if (screen === 'login') return <LoginScreen onLogin={handleLogin} onEnroll={() => setScreen('enroll')} />;
  if (screen === 'enroll') return <EnrollScreen onBack={() => setScreen('login')} onEnrolled={handleEnrolled} />;
  if (screen === 'dashboard') return <Dashboard member={member} onLogout={handleLogout} onRefresh={handleRefresh} />;
  return null;
}
